import { ListsInclusions } from '@app/data/clients/rbServerSideClient/queries/GetListInclusions/types';
import { Shop_Lists_Entity, Shop_Lists_ItemInput } from '@app/data/shop-bff';
import logger from '@redbubble/next/logger';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useCallback, useRef, useState } from 'react';
import { publicShopBffSdk } from '../utils/common';

interface InventoryId {
  id: string;
}

const mapInventoryIds = (
  inventoryItems: (InventoryId | null | undefined)[]
) => {
  return inventoryItems.reduce((acc: Shop_Lists_ItemInput[], result) => {
    if (result) {
      acc.push({
        entityId: result.id,
        entityType: Shop_Lists_Entity.InventoryItem,
      });
    }
    return acc;
  }, []);
};

export const useListsInclusions = () => {
  const [{ isLoggedIn, locale }] = useUserInfo<UserInfo>();
  const [listsInclusions, setListsInclusions] =
    useState<ListsInclusions | null>(null);

  const defaultRef = JSON.stringify([]);
  const itemRef = useRef<string>(defaultRef);

  const refresh = useCallback(
    (inventoryItems: (InventoryId | null | undefined)[]) => {
      const items = mapInventoryIds(inventoryItems);
      const itemJson = JSON.stringify(items);
      if (itemJson === defaultRef || itemJson === itemRef.current) {
        return;
      }

      itemRef.current = itemJson;
      publicShopBffSdk
        .GetListsInclusions({
          input: {
            locale,
            items,
          },
        })
        .then((res) => {
          // Shop BFF response has a slightly narrower shape (lists[].listId
          // only) than the monolith lists response (lists[].id + listId) that
          // ListsInclusions is now derived from. Cast is safe here because
          // consumers only read id / lists[].listId.
          setListsInclusions(
            (res.shop_lists_inclusions as unknown as ListsInclusions) ?? null
          );
        })
        .catch((error) => {
          const message = 'Failed to fetch lists inclusions';
          logger.error(
            { err: error, context: 'useListsInclusions', message },
            message
          );
          setListsInclusions(null);
        });
    },
    [defaultRef, locale]
  );

  if (!isLoggedIn) {
    return {
      listsInclusions: null,
      refresh: () => {},
    };
  }

  return {
    listsInclusions,
    refresh,
  };
};
